import { bankRecDateParams } from "@/views/BankReconciliation/BankRec/dates";
/* eslint-disable */
import React, { useEffect, useState, useMemo } from "react";
import { Button, Spin, Tooltip, message, Flex, Modal,Tag} from "antd";
import { MenuOutlined, BankOutlined } from "@ant-design/icons";
import { useSearchParams } from "react-router-dom";
import dayjs from "dayjs";
import moment from "moment";

import EnhancedTable from "@/hocComponents/EnhancedTable";
import http from "@/utils/http";
import api from "@/utils/api";
import moneyIcon from "@/assets/images/icons/money_1.png";
import warningIcon from "@/assets/images/icons/warning.png";
import ordersIcon from "@/assets/images/icons/orders.png";

import "./Utpage.css";

/* ------------------------------ helpers ------------------------------ */

// 金额工具（防浮点）
const toCent = (n = 0) =>
  Number.isFinite(Number(n)) ? Math.round(Number(n) * 100) : 0;
const fromCent = (c = 0) => (c / 100).toFixed(2);

function formatUSD(amount) {
  const num = Number(amount);
  const formatted = Math.abs(num).toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  return num < 0 ? `$(${formatted})` : `$${formatted}`;
}

const formatDate = (v) => moment(v).format("MM/DD/YYYY");

// 数据写入 state 后，等表格真正渲染并绘制完再关闭 loading，避免出现“loading 已结束但表格还是空的”
const afterPaint = () =>
  Promise.race([
    new Promise((resolve) =>
      requestAnimationFrame(() => requestAnimationFrame(resolve)),
    ),
    new Promise((resolve) => setTimeout(resolve, 500)),
  ]);

// 带 Tooltip 的文本单元格
const renderTip = (text) => (
  <Tooltip title={text}>
    <span>{text}</span>
  </Tooltip>
);

// 上：日期 / 下：Journal
const renderDateStack = (dateKey, emptyDate) => (_, record) => (
  <div>
    <div>{record[dateKey] ? formatDate(record[dateKey]) : emptyDate}</div>
    <div>{record.journal || " "}</div>
  </div>
);

// 上：Cleared 日期 / 下：Match ID
const renderMatchId = (_, record) => {
  const dateText =
    record.clearedDateStr && String(record.clearedDateStr).trim()
      ? formatDate(record.clearedDateStr)
      : "";
  return (
    <div>
      <div>{dateText}</div>
      <div>{record.matchId ?? ""}</div>
    </div>
  );
};

const BalanceRow = ({ label, value, bold, negative }) => (
  <div
    className={`utp-balance__row${bold ? " is-bold" : ""}${negative ? " is-negative" : ""}`}
  >
    <span>{label}</span>
    <span>{value}</span>
  </div>
);


/* ----------------------------- component ----------------------------- */

export default function UTPage() {
  const [searchParams] = useSearchParams();
  const storageKey = searchParams.get("key");
  const storedData = useMemo(() => {
    if (!storageKey) return null;
    try {
      const raw = sessionStorage.getItem(storageKey);
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  }, [storageKey]);

  const record = storedData?.record;
  const reconcileDateSt = storedData?.reconcileDateSt;
  const bankId = record?.bankId || record?.bankInfo?.bankId;

  if (!record || !reconcileDateSt || !bankId) {
    return <div>Please go back and select a bank record.</div>;
  }

  // 三块区域各自独立 loading，互不影响
  const [leftLoading, setLeftLoading] = useState(true);
  const [rightLoading, setRightLoading] = useState(true);
  const [balanceLoading, setBalanceLoading] = useState(true);
  const [clearedLoading, setClearedLoading] = useState(false);

  const [clearedList, setClearedList] = useState([]);
  const [bankTransList, setBankTransList] = useState([]);

  const [selectedLeftIds, setSelectedLeftIds] = useState([]);
  const [selectedRightIds, setSelectedRightIds] = useState([]);

  const [balanceInfo, setBalanceInfo] = useState({});
  const [clearedFilters, setClearedFilters] = useState({});
  const [bankFilters, setBankFilters] = useState({});

  /* ------------------------------ 金额汇总 ------------------------------ */

  const leftCent = useMemo(
    () =>
      selectedLeftIds.reduce((sum, id) => {
        const a = clearedList.find((r) => r.clearedTransId === id)?.amount ?? 0;
        return sum + toCent(a);
      }, 0),
    [selectedLeftIds, clearedList],
  );

  const rightCent = useMemo(
    () =>
      selectedRightIds.reduce((sum, id) => {
        const a =
          bankTransList.find((r) => r.bankTransNewId === id)?.amount ?? 0;
        return sum + toCent(a);
      }, 0),
    [selectedRightIds, bankTransList],
  );

  const submitLeftIds = useMemo(
    () =>
      selectedLeftIds
        .map(
          (id) =>
            clearedList.find((r) => r.clearedTransId === id)?.allClearedTransId,
        )
        .filter(Boolean),
    [selectedLeftIds, clearedList],
  );

  const leftAmount = formatUSD(fromCent(leftCent));
  const rightAmount = formatUSD(fromCent(rightCent));
  const differenceCent = leftCent + rightCent;
  const difference = formatUSD(fromCent(differenceCent));

  const leftCount = selectedLeftIds.length;
  const rightCount = selectedRightIds.length;
  const hasLeftSelected = leftCount > 0;
  const hasRightSelected = rightCount > 0;
  const canClear = hasLeftSelected;

  /* ------------------------------ 数据加载 ------------------------------ */

  const loadData = async () => {
    setBalanceLoading(true);
    try {
      const params = { bankId, reconcileDateSt };
      await http.get(api.getLockBankRec, { params: bankRecDateParams(params) });
      setBalanceInfo(record);
    } finally {
      setBalanceLoading(false);
    }
  };

  const loadCleared = async (filters = {}) => {
    setLeftLoading(true);
    try {
      const params = { bankId, reconcileDateSt, ...filters };
      const res = await http.get(api.getClearedTransaction, { params: bankRecDateParams(params) });
      setClearedList(res?.data || []);
      await afterPaint();
    } finally {
      setLeftLoading(false);
    }
  };

  const loadBank = async (filters = {}) => {
    setRightLoading(true);
    try {
      const params = { bankId, reconcileDateSt, ...filters };
      const res = await http.get(api.getBankTransaction, { params: bankRecDateParams(params) });
      setBankTransList(res?.data || []);
      await afterPaint();
    } finally {
      setRightLoading(false);
    }
  };

  useEffect(() => {
    loadData();
    loadCleared();
    loadBank();
    return () => {
      setClearedList([]);
      setBankTransList([]);
      setSelectedLeftIds([]);
      setSelectedRightIds([]);
    };
  }, [bankId, reconcileDateSt]);

  /* -------------------------------- 搜索 -------------------------------- */

  const handleClearedFilterChange = (key, value) =>
    setClearedFilters((prev) => ({ ...prev, [key]: value }));

  const handleBankFilterChange = (key, value) =>
    setBankFilters((prev) => ({ ...prev, [key]: value }));

  const handleClearedFilterSubmit = (nextFilters) => loadCleared(nextFilters);
  const handleBankFilterSubmit = (nextFilters) => loadBank(nextFilters);

  /* -------------------------------- 清算 -------------------------------- */

  const doClearSelected = async () => {
    setClearedLoading(true);
    try {
      const params = {
        clearedTransIds: submitLeftIds.join(","),
        bankTransNewIds: selectedRightIds.join(","),
        reconcileDateSt,
      };
      const res = await http.get(api.getReconcileTransaction, { params: bankRecDateParams(params) });

      if (res.code === "1") {
        message.success("Reconcile success!");
        loadData();
        loadCleared();
        loadBank();
      } else {
        message.error(res.msg || "Reconcile failed");
      }
    } finally {
      setClearedLoading(false);
    }
  };

  const handleClearSelected = () => {
    if (!canClear) {
      return message.error("Please select matched Book and Bank transactions");
    }

    Modal.confirm({
      title: "Confirm Reconcile",
      content: "You are about to reconcile the selected transactions.",
      okText: "Confirm",
      cancelText: "Close",
      centered: true,
      onOk: doClearSelected,
    });
  };

  /* -------------------------------- 列配置 -------------------------------- */

  // 左表：Book
  const clearedColumns = [
    {
      title: "Date",
      dataIndex: "date1",
      filterType: "date",
      filterKey: "date1",
      width: 120,
      render: renderDateStack("date1", " "),
    },
    { title: "Check #", dataIndex: "checkNo", width: 90, filterKey: "checkNo" },
    {
      title: "Amount",
      dataIndex: "amountRow",
      width: 110,
      className: "utp-num",
      render: (_, r) => {
        const tip = r.amountHtml;
        return (
          <span style={{ display: "inline-flex", gap: 2 }}>
            <span>{r.amountRow}</span>
            {tip && (
              <Tooltip
                title={
                  <div
                    dangerouslySetInnerHTML={{ __html: tip }}
                    style={{
                      maxWidth: 450,
                      whiteSpace: "normal",
                      wordBreak: "break-word",
                      lineHeight: "1.6",
                    }}
                  />
                }
              >
                <MenuOutlined style={{ color: "#1677ff" }} />
              </Tooltip>
            )}
          </span>
        );
      },
    },
    { title: "Batch #", dataIndex: "batch", width: 120, filterKey: "batch" },
    {
      title: "GL- Description",
      dataIndex: "glAcct",
      width: 120,
      ellipsis: true,
      render: renderTip,
    },
    {
      title: "Payee",
      dataIndex: "name",
      width: 100,
      ellipsis: true,
      filterKey: "name",
      render: renderTip,
    },
    {
      title: "Remark",
      dataIndex: "remark",
      ellipsis: true,
      filterKey: "remark",
      render: renderTip,
    },
    {
      title: "Match ID",
      dataIndex: "clearedDateStr",
      width: 110,
      filterKey: "matchIdOrClearedDate",
      render: renderMatchId,
    },
  ];

  // 右表：Bank
  const bankColumns = [
    {
      title: "Date",
      dataIndex: "date",
      filterKey: "date",
      filterType: "date",
      width: 100,
      render: renderDateStack("date", "-"),
    },
    { title: "Check #", dataIndex: "checkNo", filterKey: "checkNo", width: 90 },
    {
      title: "Amount",
      dataIndex: "amountRow",
      filterKey: "amountRow",
      width: 110,
      className: "utp-num",
    },
    {
      title: "Description",
      dataIndex: "remark",
      filterKey: "remark",
      width: 180,
      ellipsis: true,
      render: renderTip,
    },
    { title: "Bai Code ", dataIndex: "baiCode", filterKey: "baiCode", width: 100 },
    {
      title: "Match ID",
      dataIndex: "clearedDateStr",
      width: 110,
      filterKey: "matchIdOrClearedDate",
      render: renderMatchId,
    },
  ];

  const rowSelection = (selectedRowKeys, onChange) => ({
    selectedRowKeys,
    onChange,
    getCheckboxProps: (r) => ({ disabled: !!r.clearedDateStr }),
    columnWidth: 30,
  });

  const metaItems = [
    ["ENTITY", record?.bankInfo?.trustName],
    ["OWNER", record?.bankInfo?.ownerName],
    ["GL", record?.bankInfo?.glAcct],
    ["BANK", record?.bankInfo?.name],
    ["ACCOUNT", record?.bankInfo?.crAcct],
    ["CLEAR DATE", dayjs(reconcileDateSt).format("MM/DD/YYYY")],
  ];

  /* -------------------------------- Render -------------------------------- */

  return (
    <div className="utp-page">
      <header className="utp-header">
        <h1 className="utp-title">
      <BankOutlined />
       Unreconciled Transactions
     </h1>
        <div className="utp-meta">
          {metaItems.map(([label, value]) => (
            <span className="utp-meta__item" key={label}>
              <span className="utp-meta__label">{label}</span>
              <span className="utp-meta__value">{value}</span>
            </span>
          ))}
        </div>
      </header>

      <Flex gap={16} align="stretch" className="utp-body">
        {/* 左侧 Book */}
        <section className="utp-panel utp-panel--book">
          <div className="utp-panel__title">Book Transactions</div>
          <EnhancedTable
            rowKey="clearedTransId"
            columns={clearedColumns}
            dataSource={clearedList}
            loading={leftLoading}
            filters={clearedFilters}
            onFilterChange={handleClearedFilterChange}
            onFilterSubmit={handleClearedFilterSubmit}
            rowSelection={rowSelection(selectedLeftIds, setSelectedLeftIds)}
            scroll={{ y: "calc(100vh - 240px)" }}
            size="small"
          />
        </section>

        {/* 中间汇总 */}
        <div className="utp-summary">
          <div className="utp-card">
            <div className="utp-card__header">
              <img src={ordersIcon} className="utp-card__icon" />
              <span className="utp-card__title">Summary Selected Transaction</span>
            </div>

            <div className="utp-boxes">
              <div className="utp-box">
                <div className="utp-box__label">
                  Book Trans <span className="utp-box__count">{leftCount}</span>
                </div>
                <div className="utp-box__value">{leftAmount}</div>
              </div>
              <div className="utp-box">
                <div className="utp-box__label">
                  Bank Trans <span className="utp-box__count">{rightCount}</span>
                </div>
                <div className="utp-box__value">{rightAmount}</div>
              </div>
            </div>

            <div className="utp-diff">
              <div className="utp-diff__label">Current Difference</div>
              <div
                className={`utp-diff__value${differenceCent === 0 ? " is-zero" : ""}`}
              >
                {difference}
              </div>

              {!canClear && hasRightSelected && (
                <div className="utp-diff__warning">
                  <img src={warningIcon} className="utp-card__icon" />
                  Please select at least one Book transaction
                </div>
              )}
            </div>

   <Button
  type="primary"
  block
  size="large"
  disabled={!canClear}
  loading={clearedLoading}
  onClick={handleClearSelected}
>
  Clear Select Trans
</Button>
          </div>

          {/* Balance Information */}
          <div className="utp-card utp-card--grow">
            <div className="utp-card__header">
              <img src={moneyIcon} className="utp-card__icon" />
              <span className="utp-card__title">Balance Information</span>
              <span className="utp-card__date">
                {dayjs(balanceInfo.date1).format("MM/DD/YYYY")}
              </span>
            </div>

        <Spin spinning={balanceLoading}>
  <div className="utp-balance">
    <div className="utp-balance__group">
      <BalanceRow label="Book Deposit (CR)" value={balanceInfo.bookOutStandingDepositStr} />
      <BalanceRow label="Book Check (CP)" value={balanceInfo.bookOutStandingCheckStr} />
      <BalanceRow label="Bank Deposit (CR)" value={balanceInfo.bankOutStandingDepositStr} />
      <BalanceRow label="Bank Check (CP)" value={balanceInfo.bankOutStandingCheckStr} negative />
    </div>

    <div className="utp-balance__group">
      <BalanceRow label="Total Bank and Book" value={balanceInfo.outStandingStr} bold />
    </div>

    <div className="utp-balance__group">
      <BalanceRow
        label={`GL ${balanceInfo.bankInfo?.glAcct} Balance :`}
        value={balanceInfo.glbalanceStr}
      />
    </div>

    <div className="utp-balance__group">
      <BalanceRow label="Book Reconciled to Bank Balance" value={balanceInfo.bookBalanceStr} />
      <BalanceRow label="Bank Balance" value={balanceInfo.bankBalanceStr} />
      <BalanceRow label="Bank Difference" value={balanceInfo.differenceStr} negative />
    </div>
  </div>
</Spin>
          </div>
        </div>

        {/* 右侧 Bank */}
        <section className="utp-panel utp-panel--bank">
          <div className="utp-panel__title">Bank Transactions</div>
          <EnhancedTable
            rowKey="bankTransNewId"
            columns={bankColumns}
            dataSource={bankTransList}
            loading={rightLoading}
            filters={bankFilters}
            onFilterChange={handleBankFilterChange}
            onFilterSubmit={handleBankFilterSubmit}
            rowSelection={rowSelection(selectedRightIds, setSelectedRightIds)}
            scroll={{ y: "calc(100vh - 240px)" }}
            size="small"
          />
        </section>
      </Flex>
    </div>
  );
}
